import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { courseService } from '../services/api';
import { getPhaseColor } from '../theme/phases';
import { useAuth } from './AuthContext';

const CourseContext = createContext();

export function CourseProvider({ children }) {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all courses
  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await courseService.getAllCourses();
      setCourses(data);

      // If no current course is set and there's a saved slug in localStorage, use it
      const savedSlug = localStorage.getItem('currentCourseSlug');
      if (!currentCourse && savedSlug) {
        const savedCourse = data.find(c => c.slug === savedSlug);
        if (savedCourse) {
          setCurrentCourse(savedCourse);
        }
      }
    } catch (err) {
      console.error('Error loading courses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentCourse]);

  // Os cursos dependem de quem está logado (cada usuário vê só os liberados)
  useEffect(() => {
    if (user && !user.mustChangePassword) {
      loadCourses();
    } else {
      // Sem sessão não há cursos a mostrar; fica "carregando" até o login trazer a lista
      setCourses([]);
      setCurrentCourse(null);
      setLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.mustChangePassword]);

  // Select a course by slug
  const selectCourse = useCallback(async (slug) => {
    try {
      // First check if we already have it in the list
      let course = courses.find(c => c.slug === slug);

      if (!course) {
        // Fetch from API if not in list
        course = await courseService.getCourseBySlug(slug);
      }

      if (course) {
        setCurrentCourse(course);
        localStorage.setItem('currentCourseSlug', slug);
      }
      return course;
    } catch (err) {
      console.error('Error selecting course:', err);
      setError(err.message);
      return null;
    }
  }, [courses]);

  // Clear current course selection
  const clearCourse = useCallback(() => {
    setCurrentCourse(null);
    localStorage.removeItem('currentCourseSlug');
  }, []);

  // Parse phases config from current course
  const getPhasesConfig = useCallback(() => {
    if (!currentCourse?.phasesConfig) {
      return {};
    }
    try {
      return JSON.parse(currentCourse.phasesConfig);
    } catch {
      return {};
    }
  }, [currentCourse]);

  // Get phases as array for rendering
  const getPhases = useCallback(() => {
    const config = getPhasesConfig();
    const count = currentCourse?.phasesCount || 5;
    return Array.from({ length: count }, (_, i) => {
      const num = i + 1;
      const phaseConfig = config[num] || {};
      return {
        num,
        name: phaseConfig.name || `Fase ${num}`,
        color: getPhaseColor(num),
      };
    });
  }, [currentCourse, getPhasesConfig]);

  const value = {
    courses,
    currentCourse,
    loading,
    error,
    loadCourses,
    selectCourse,
    clearCourse,
    getPhasesConfig,
    getPhases,
  };

  return (
    <CourseContext.Provider value={value}>
      {children}
    </CourseContext.Provider>
  );
}

export function useCourse() {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
}

export { CourseContext };
export default CourseContext;
